import { authenticatedFetch } from './api';
import type { HireReceipt } from '@/types/hire';
export const HIRE_ACCESS_CHANGED = 'hire-access-changed';
export class HireError extends Error {
  constructor(
    public status: number,
    public code: string,
  ) {
    const messages: Record<string, string> = {
      scanner_unavailable: 'File checking is unavailable. No replacement has been confirmed.',
      owner_not_authorized: 'The owner must be a currently authorized superadmin.',
      clean_resume_required: 'A usable clean resume is required before interview approval.',
      review_terms_required: 'Save a start date and authorized owner before submitting this case.',
      operation_expired:
        'This file operation has expired. Refresh to confirm the current record before starting a replacement.',
      upload_missing: 'The uploaded file is not available yet. Retry this operation.',
      undo_unavailable: 'This record changed after the page loaded, so that Undo no longer applies. Refresh to see what Undo does now.',
    };
    super(
      messages[code] ??
        (status === 403 || status === 401
          ? 'Hiring access is no longer available.'
          : status === 409
            ? 'This record changed or the action is still processing. Refresh and review before retrying.'
            : status === 422
              ? 'This command cannot be reused with changed details. Review and try again.'
              : status === 503
                ? 'Hiring is temporarily unavailable. Your changes have not been confirmed.'
                : 'This action could not be completed. Check the details and try again.'),
    );
  }
}
export async function hireRead<T>(path: string, signal?: AbortSignal): Promise<T> {
  return request<T>(path, { signal });
}
async function request<T>(path: string, options: RequestInit): Promise<T> {
  const response = await authenticatedFetch(`/api/v1/admin/hire${path}`, { ...options, cache: 'no-store' });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    if (
      path !== '/capabilities' &&
      (response.status === 401 || response.status === 403 || data.code === 'hire_authority_unavailable')
    )
      window.dispatchEvent(new Event(HIRE_ACCESS_CHANGED));
    throw new HireError(response.status, typeof data.code === 'string' ? data.code : 'request_failed');
  }
  return data as T;
}
/** The caller keeps the same key for an uncertain retry; changed commands get new keys. */
export function hireWrite<T = HireReceipt>(
  path: string,
  body: Record<string, unknown>,
  key: string,
  method = 'POST',
): Promise<T> {
  return request<T>(path, {
    method,
    headers: { 'Content-Type': 'application/json', 'Idempotency-Key': key },
    body: JSON.stringify(body),
  });
}
